import { z } from "zod";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/shared/lib/utils";
import {
  ChartSkeleton,
  CHART_TOOLTIP,
  CHART_GRID,
  CHART_AXIS_TICK,
} from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const BidChartSchema = z.object({
  items: z.array(
    z.object({
      keyword: z.string(),
      bid: z.number(),
      competition: z.enum(["HIGH", "MEDIUM", "LOW"]).optional(),
    }),
  ),
  currency: z.string().optional(),
});

type BidChartData = z.infer<typeof BidChartSchema>;

/* ────────────────────── Helpers ────────────────────── */

const COMPETITION_COLOR: Record<string, string> = {
  HIGH: "hsl(var(--signal-danger))",
  MEDIUM: "hsl(var(--signal-warning))",
  LOW: "hsl(var(--signal-success))",
};

const DEFAULT_BAR_COLOR = "hsl(var(--accent))";

function getBarColor(competition?: string): string {
  if (!competition) return DEFAULT_BAR_COLOR;
  return COMPETITION_COLOR[competition] ?? DEFAULT_BAR_COLOR;
}

/** 통화 단위 포맷 */
function formatBid(value: number, currency = "원"): string {
  return `${value.toLocaleString()}${currency}`;
}

/* ────────────────────── Custom Tooltip ────────────────────── */

interface TooltipPayloadEntry {
  value: number;
  payload: { keyword: string; bid: number; competition?: string };
}

function BidTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  currency?: string;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div
      style={CHART_TOOLTIP.contentStyle}
      className="bg-surface-raised px-3 py-2 shadow-md"
    >
      <p className="text-[11px] font-semibold text-text-primary">
        {item.keyword}
      </p>
      <p className="mt-0.5 text-[11px] text-text-tertiary">
        입찰가:{" "}
        <span className="font-medium text-text-primary">
          {formatBid(item.bid, currency)}
        </span>
      </p>
      {item.competition && (
        <p
          className="text-[10px]"
          style={{ color: getBarColor(item.competition) }}
        >
          경쟁도: {item.competition}
        </p>
      )}
    </div>
  );
}

/* ────────────────────── Component ────────────────────── */

export function BidChart({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<BidChartData>) {
  const { items, currency = "원" } = widget.data;
  const hasDeepLink = !!(widget.deepLink && onDeepLink);

  return (
    <div
      className={cn("w-full", className)}
      style={{ height: 240 }}
      onClick={hasDeepLink ? () => onDeepLink!(widget.deepLink!) : undefined}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={items}
          margin={{ top: 4, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis
            dataKey="keyword"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => v.toLocaleString()}
          />
          <Tooltip content={<BidTooltip currency={currency} />} />
          <Bar dataKey="bid" radius={[4, 4, 0, 0]} maxBarSize={48}>
            {items.map((item, idx) => (
              <Cell
                key={idx}
                fill={getBarColor(item.competition)}
                fillOpacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* 경쟁도 범례 */}
      {items.some((i) => i.competition) && (
        <div className="mt-1 flex items-center justify-end gap-3 pr-2">
          {(["HIGH", "MEDIUM", "LOW"] as const).map((level) => (
            <div key={level} className="flex items-center gap-1">
              <span
                className="inline-block size-2 rounded-full"
                style={{ background: COMPETITION_COLOR[level] }}
              />
              <span className="text-[10px] text-text-tertiary">{level}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function BidChartSkeleton({ className }: { className?: string }) {
  return <ChartSkeleton height={240} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const bidChartWidget = defineWidget<BidChartData>({
  type: WIDGET_TYPES.BID_CHART,
  label: "입찰가 BarChart",
  schema: BidChartSchema,
  component: BidChart,
  skeleton: BidChartSkeleton,
  defaultLayout: "full",
  maxHeight: 280,
  aiHint: "키워드별 입찰가와 경쟁도를 바 차트로 표현",
});

registerWidget(bidChartWidget);
